import React, { CSSProperties, useEffect, useState } from "react";
import SVG from "react-inlinesvg";
import { detect } from "detect-browser";
import { StyledHelpMenu } from "./styles";
import { sendAppEvent, DISCORD_LINK } from "../../Utils";
import Auth from "../../Auth";
import firebase from "../../Firebase.js";
import useInertRef from "../../hooks/useInertRef";
import usePopoverClose from "../../hooks/usePopoverClose";
import { Button } from "../Button/index.js";
import { AnimatePresence, motion } from "framer-motion";
import StickyNoteAnchor from "../StickyTour/StickyNoteAnchor";
import { useStickyTourStore } from "../../stores/stickyTourStore";
import { AnchorIds, WriteRewriteTourVariations, StoryBibleTourVariations } from "../StickyTour/TourTypes";
import { getWriteRewriteTour } from "../StickyTour/Tours/WriteRewrite/WriteRewriteTour";
import { getStoryBibleTour } from "../StickyTour/Tours/StoryBibleTour";
import { useSudowriteStore } from "../../stores/sudowriteStore";

const AIRTABLE_SUPPORT_FORM_URL = "https://airtable.com/app1ghrttIhe7RsQq/shrlPKZOittXtOwhi";

/** Airtable "Invocation ID" field (form label: Activity ID). */
const AIRTABLE_ACTIVITY_ID_FIELD_ID = "fldfuPMNNF9E66XWS";

interface Subscription {
  data: {
    email?: string;
  };
}

export type GetAirTableSupportUrlOptions = {
  /** Prefills the form's Activity ID (Invocation ID) field. */
  activityId?: string | null;
};

const buildAirtablePrefillParams = (
  subscription: Subscription | null | undefined,
  options?: GetAirTableSupportUrlOptions,
) => {
  const browser = detect();
  let prefillValues: Record<string, string> = {
    url: window.location.href,
    account: subscription?.data?.email || "",
    platform: "Web",
  };

  if (browser) {
    prefillValues = {
      ...prefillValues,
      browser: browser.name || "",
      browserVersion: browser.version || "",
      os: browser.os || "",
    };
  } else {
    prefillValues = {
      ...prefillValues,
      browser: window.navigator.userAgent,
    };
  }

  const activityId = options?.activityId?.trim();
  if (activityId) {
    prefillValues = {
      ...prefillValues,
      activityId,
    };
  }

  const airTableFieldIds: Record<string, string> = {
    browser: "fld0v7KRsCmNaEYAo",
    browserVersion: "fldZNMt5z67qYV0Ai",
    os: "fldLh52fOOgHhLwRh",
    url: "fldS8hJvSK9JinlXT",
    account: "fldP4bPiekQVpNNhH",
    platform: "fld4FaZBpxKrWr5JB",
    activityId: AIRTABLE_ACTIVITY_ID_FIELD_ID,
  };

  const prefillData = Object.fromEntries(
    Object.entries(prefillValues)
      .filter(([key, value]) => Boolean(value) && Boolean(airTableFieldIds[key]))
      .map(([key, value]) => [`prefill_${airTableFieldIds[key]}`, value]),
  );

  const hideParams = Object.fromEntries(
    ["url", "browser", "browserVersion", "os", "platform"].map((key) => [`hide_${airTableFieldIds[key]}`, "true"]),
  );

  const searchParams = new URLSearchParams({ ...prefillData, ...hideParams });
  return `${AIRTABLE_SUPPORT_FORM_URL}?${searchParams.toString()}`;
};

export const getAirTableSupportUrl = (
  subscription: Subscription | null | undefined,
  options?: GetAirTableSupportUrlOptions,
) => {
  return buildAirtablePrefillParams(subscription, options);
};

export const openAirTableForm = (
  subscription: Subscription | null | undefined,
  options?: GetAirTableSupportUrlOptions,
) => {
  const url = getAirTableSupportUrl(subscription, options);
  window.open(url);
};

// Builds a dynamic mailto URL for contacting support with context
const buildEmailUsUrl = (subscription: Subscription | null | undefined) => {
  const userEmail = subscription?.data?.email || firebase.auth().currentUser?.email || "";

  // Detect platform and app version (for native apps if present)
  const ua = navigator.userAgent || "";
  let platform = "web";
  let appVersion: string | null = null;

  if (/Android/i.test(ua)) {
    platform = "android";
  } else if (/iPhone|iPad|iPod/i.test(ua)) {
    platform = "ios";
  } else if (/Mobi|Tablet/i.test(ua)) {
    platform = "other";
  }

  // Try to extract a native app version token from UA (e.g., "Sudowrite/1.2.3")
  const appVersionMatch = ua.match(/Sudowrite\/(\d+[\._]?\d*[\._]?\d*)/i);
  if (appVersionMatch && (platform === "ios" || platform === "android")) {
    appVersion = appVersionMatch[1].replace(/_/g, ".");
  }

  // Fallback to client version if available and on mobile platforms
  if (!appVersion && (platform === "ios" || platform === "android") && (window as any).sudowriteClientVersion) {
    appVersion = (window as any).sudowriteClientVersion;
  }

  // OS version detection
  const osVersion = (() => {
    // Android 14, iOS 17.5.1, macOS 14.5, Windows 10.0, etc.
    const android = ua.match(/Android\s([\d\.]+)/i);
    if (android) return `Android ${android[1]}`;

    const ios = ua.match(/OS\s([\d_]+)\slike\sMac\sOS\sX/i);
    if (ios) return `iOS ${ios[1].replace(/_/g, ".")}`;

    const mac = ua.match(/Mac OS X\s([\d_\.]+)/i);
    if (mac) return `macOS ${mac[1].replace(/_/g, ".")}`;

    const windows = ua.match(/Windows NT\s([\d\.]+)/i);
    if (windows) return `Windows ${windows[1]}`;

    const browser = detect();
    if (browser?.os) return browser.os;

    return "unknown";
  })();

  const subject = `[via ${platform}] `; // note trailing space per spec

  const lines = [
    "Hi Sudowrite Team,",
    "",
    "[Write your question, feedback, or issue here]",
    "",
    "",
    "---",
    "",
    `Account: ${userEmail || ""}`,
    "",
    appVersion && (platform === "ios" || platform === "android") ? `Platform: ${platform} (v${appVersion})` : `Platform: ${platform}`,
    "",
    `OS: ${osVersion}`,
  ];

  const body = lines.join("\n");
  const to = "hi@sudowrite.com";
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

interface HelpMenuProps {
  subscription: Subscription | null | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
  style?: CSSProperties;
  showFront: (open: boolean) => void;
  /** When set and user has agentic chat access, Help → Chat opens in-app agentic chat instead of legacy front chat. */
  openAgenticChatFromHelp?: () => void;
  v2?: boolean;
}

const HelpMenu = ({ subscription, open, setOpen, style, showFront, openAgenticChatFromHelp, v2 }: HelpMenuProps) => {
  const [sleekToken, setSleekToken] = useState<string | null>(null);
  const { startTour, anchors } = useStickyTourStore();
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);

  const menuInertRef = useInertRef(open);
  const { containerRef, handleBlur } = usePopoverClose(open, () => setOpen(false), {
    onEscapeFocus: () => (containerRef.current as HTMLElement | null)?.querySelector("button")?.focus(),
  });

  useEffect(() => {
    let isMounted = true;

    Auth.fetchWithIdToken("/api/sleekplan/sso", {}).then((result: any) => {
      if (isMounted) {
        setSleekToken(result.token);

        if ((window as any).$sleek.setUser) {
          (window as any).$sleek.setUser({
            token: result.token,
            name: firebase.auth().currentUser?.displayName,
          });
        }
      }
    });

    return () => { isMounted = false; }; // set isMounted to false when component unmounts
  }, []);

  return (
    <StyledHelpMenu style={style}>
      <StickyNoteAnchor anchorId={AnchorIds.HELP_ICON} />
      <div
        id="help-container"
        className={open ? "help-open" : undefined}
        ref={containerRef}
        onBlur={handleBlur}
      >
        <Button
          secondaryGrey={true}
          className="use-component-styles"
          aria-label="Help Menu"
          aria-haspopup="true"
          aria-expanded={open}
          iconSize={24}
          onClick={() => {
            setOpen(!open);
          }}
        >
          <SVG src={v2 ? "/assets/help-icon-v2.svg" : "/assets/help-icon.svg"} />
        </Button>
        <div ref={menuInertRef}>
        <AnimatePresence>
          {open && (
            <motion.div
              transition={{ duration: 0.15 }}
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              className="help"
            >
              <div className="section">
                <span>Resources</span>
                <div className="button-grid">
                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      sendAppEvent("Click Live Classes", { source: "Help Menu" });
                      window.open("https://lu.ma/sudowrite/", "_blank");
                    }}
                  >
                    Live Classes
                  </Button>
                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      sendAppEvent("Click Join Discord", { source: "Help Menu" });
                      window.open(DISCORD_LINK, "_blank");
                    }}
                  >
                    Discord
                  </Button>
                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      sendAppEvent("Click Help Guide", { source: "Help Menu" });
                      window.open("https://docs.sudowrite.com", "_blank");
                    }}
                  >
                    User Guide
                  </Button>
                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      sendAppEvent("Click Youtube", { source: "Help Menu" });
                      window.open("https://www.youtube.com/@sudowrite4400", "_blank");
                    }}
                  >
                    Youtube
                  </Button>
                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      const hashParams = new URLSearchParams(window.location.hash.substring(1));
                      const documentId = hashParams.get("doc");

                      // If we are inside a project, start at the in-project chat onboarding step
                      const options = documentId ? { startAtStepId: "1.1" } : undefined;
                      startTour(getWriteRewriteTour(WriteRewriteTourVariations.FROM_HELP_MENU), options);

                      setOpen(false);
                    }}
                  >
                    Quickstart Tour
                  </Button>
                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      const hashParams = new URLSearchParams(window.location.hash.substring(1));
                      const documentId = hashParams.get("doc");

                      if (documentId) {
                        // If we're in a project, scroll to Story Bible and start from step 0.1
                        const storyBibleAnchor = anchors[AnchorIds.STORY_BIBLE_SEED];
                        if (storyBibleAnchor?.current) {
                          storyBibleAnchor.current.scrollIntoView({ behavior: "smooth" });
                        }
                        startTour(getStoryBibleTour(StoryBibleTourVariations.FROM_HELP_MENU), { startAtStepId: "0.1" });
                      } else {
                        startTour(getStoryBibleTour(StoryBibleTourVariations.FROM_HELP_MENU));
                      }
                      setOpen(false);
                    }}
                  >
                    Story Bible Tour
                  </Button>
                </div>
              </div>
              <div className="section">
                <span>Get in touch</span>
                <div className="button-grid">
                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    disabled={sleekToken === null}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      sendAppEvent("Click Suggest a Feature", { source: "Help Menu" });
                      window.open("https://feedback.sudowrite.com", "_blank");
                    }}
                  >
                    Suggest a Feature
                  </Button>
                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      sendAppEvent("Click Changelog", { source: "Help Menu" });
                      window.open("https://feedback.sudowrite.com/changelog", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Changelog
                  </Button>

                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      window.open(buildEmailUsUrl(subscription));
                    }}
                  >
                    <nobr>Email Us</nobr>
                  </Button>
                  <Button
                    className="use-component-styles"
                    tertiaryBorder={true}
                    onClick={() => openAirTableForm(subscription)}
                  >
                    Report a Bug
                  </Button>
                </div>
              </div>

              <div className="footer">
                <a href="https://www.sudowrite.com/privacy-policy">Privacy Policy</a>
                <a href="https://www.sudowrite.com/terms-and-conditions">Terms of Use</a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </StyledHelpMenu>
  );
};

export default HelpMenu;
